import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, RefreshCw, MoreVertical, Users } from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

const PLANS = ["", "free", "starter", "pro", "business", "enterprise"];
const ROLES = ["", "user", "admin"];
const STATUSES = ["", "active", "suspended", "deleted"];

function Badge({ children, tone = "neutral" }) {
    const tones = {
        neutral: "bg-white/5 text-text-secondary border-[var(--color-border)]",
        primary: "bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30",
        success: "bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/30",
        warning: "bg-neutral-400/10 text-neutral-400 border-neutral-400/30",
        danger: "bg-[var(--color-alert)]/10 text-[var(--color-alert)] border-[var(--color-alert)]/30",
    };
    return (
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${tones[tone]}`}>
            {children}
        </span>
    );
}

function planTone(p) {
    if (p === "free") return "neutral";
    if (p === "starter") return "primary";
    if (p === "pro") return "success";
    if (p === "business") return "warning";
    if (p === "enterprise") return "danger";
    return "neutral";
}

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(20);
    const [q, setQ] = useState("");
    const [filters, setFilters] = useState({ plan_id: "", role: "", status: "", reputation_min: "", reputation_max: "" });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            p.set("limit", String(limit));
            p.set("offset", String(offset));
            if (q) p.set("q", q);
            for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v);
            const res = await apiFetch(`/admin/users?${p}`);
            setRows(res.users || []);
            setTotal(res.total || 0);
        } catch (e) { toast.error(e.message || "Load failed"); }
        finally { setLoading(false); }
    }, [q, filters, offset, limit]);

    useEffect(() => { load(); }, [load]);

    const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);

    const bulkNotify = async () => {
        if (!selectedIds.length) return;
        const title = prompt("Notification title:");
        if (!title) return;
        const body = prompt("Notification body:");
        if (!body) return;
        const plans = [...new Set(rows.filter((r) => selected[r.id]).map((r) => r.plan_id))].filter(Boolean);
        try {
            const res = await apiFetch("/admin/users/bulk-notify", {
                method: "POST",
                body: JSON.stringify({
                    segment: { plan_ids: plans.length ? plans : undefined },
                    notification: { title, body },
                }),
            });
            toast.success(`Sent ${res.sent}/${res.recipients_matched}`);
            setSelected({});
        } catch (e) { toast.error(e.message); }
    };

    if (!isAdminRole(me?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center">
                <h1 className="text-2xl font-semibold">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-7xl mx-auto w-full" data-testid="admin-users-list">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                User management
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold">Users · {total.toLocaleString()}</h1>
                        </div>
                        <button
                            onClick={load}
                            data-testid="users-refresh"
                            className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1"
                        >
                            <RefreshCw className="h-3 w-3" /> Refresh
                        </button>
                    </div>

                    {/* Filters */}
                    <div className="grid gap-3 md:grid-cols-6 mb-6">
                        <div className="md:col-span-2 relative">
                            <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-text-secondary pointer-events-none" />
                            <input
                                data-testid="users-search"
                                placeholder="Email or name…"
                                value={q}
                                onChange={(e) => { setOffset(0); setQ(e.target.value); }}
                                className="w-full rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] pl-8 pr-3 py-2 text-sm text-text-main focus:border-[var(--color-primary)] outline-none"
                            />
                        </div>
                        <select
                            data-testid="users-filter-plan"
                            value={filters.plan_id}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, plan_id: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main"
                        >
                            {PLANS.map((p) => <option key={p || "any"} value={p}>{p ? `Plan: ${p}` : "Any plan"}</option>)}
                        </select>
                        <select
                            data-testid="users-filter-role"
                            value={filters.role}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, role: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main"
                        >
                            {ROLES.map((r) => <option key={r || "any"} value={r}>{r ? `Role: ${r}` : "Any role"}</option>)}
                        </select>
                        <select
                            data-testid="users-filter-status"
                            value={filters.status}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, status: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main"
                        >
                            {STATUSES.map((s) => <option key={s || "any"} value={s}>{s ? `Status: ${s}` : "Any status"}</option>)}
                        </select>
                        <input
                            data-testid="users-filter-rep-min"
                            placeholder="Rep ≥"
                            type="number"
                            value={filters.reputation_min}
                            onChange={(e) => { setOffset(0); setFilters((s) => ({ ...s, reputation_min: e.target.value })); }}
                            className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] px-3 py-2 text-sm text-text-main"
                        />
                    </div>

                    {/* Bulk toolbar */}
                    {selectedIds.length > 0 && (
                        <div
                            data-testid="bulk-action-toolbar"
                            className="mb-4 rounded-xl border border-[var(--color-primary)]/40 bg-[var(--color-panel-ai)] px-4 py-2 flex items-center justify-between"
                        >
                            <span className="text-xs text-text-main">{selectedIds.length} selected</span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={bulkNotify}
                                    data-testid="bulk-notify-btn"
                                    className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-3 py-1"
                                >
                                    Bulk notify
                                </button>
                                <button
                                    onClick={() => setSelected({})}
                                    className="text-xs text-text-secondary hover:text-text-main"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[10px] uppercase tracking-widest text-text-secondary bg-[var(--color-panel-ai)]">
                                        <tr>
                                            <th className="text-left py-2 px-3 w-8"></th>
                                            <th className="text-left py-2 px-3">Email</th>
                                            <th className="text-left py-2 px-3">Name</th>
                                            <th className="text-left py-2 px-3">Plan</th>
                                            <th className="text-right py-2 px-3">Credits</th>
                                            <th className="text-right py-2 px-3">Rep</th>
                                            <th className="text-left py-2 px-3">Role</th>
                                            <th className="text-left py-2 px-3">Created</th>
                                            <th className="text-right py-2 px-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((u) => (
                                            <tr
                                                key={u.id}
                                                data-testid={`user-row-${u.id}`}
                                                className="border-t border-[var(--color-border)]/60 hover:bg-white/5"
                                            >
                                                <td className="py-2 px-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selected[u.id]}
                                                        onChange={() => toggle(u.id)}
                                                        data-testid={`user-select-${u.id}`}
                                                    />
                                                </td>
                                                <td className="py-2 px-3">
                                                    <Link
                                                        to={`/admin/users/${u.id}`}
                                                        data-testid={`user-open-${u.id}`}
                                                        className="text-text-main hover:text-[var(--color-primary)] truncate max-w-[240px] inline-block"
                                                    >
                                                        {u.email}
                                                    </Link>
                                                </td>
                                                <td className="py-2 px-3 text-text-secondary truncate max-w-[160px]">{u.name || "—"}</td>
                                                <td className="py-2 px-3"><Badge tone={planTone(u.plan_id)}>{u.plan_id || "free"}</Badge></td>
                                                <td className="py-2 px-3 text-right tabular-nums">{(u.credits ?? 0).toLocaleString()}</td>
                                                <td className="py-2 px-3 text-right tabular-nums text-text-secondary">{u.reputation ?? 0}</td>
                                                <td className="py-2 px-3">
                                                    {isAdminRole(u.role) ? <Badge tone="warning">admin</Badge> : <Badge>user</Badge>}
                                                </td>
                                                <td className="py-2 px-3 text-text-secondary tabular-nums">{(u.created_at || "").slice(0, 10)}</td>
                                                <td className="py-2 px-3 text-right">
                                                    <Link
                                                        to={`/admin/users/${u.id}`}
                                                        className="text-text-secondary hover:text-text-main"
                                                        title="Open"
                                                    >
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                        {rows.length === 0 && (
                                            <tr><td colSpan={9} className="py-10 text-center text-text-secondary">
                                                <Users className="h-6 w-6 mx-auto mb-2 opacity-40" />
                                                No users match your filters.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 flex items-center justify-between">
                        <span className="text-xs text-text-secondary">
                            {offset + 1}-{Math.min(offset + rows.length, total)} of {total}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - limit))}
                                data-testid="users-prev"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary px-4 py-1.5 disabled:opacity-40"
                            >
                                ← Prev
                            </button>
                            <button
                                disabled={offset + limit >= total}
                                onClick={() => setOffset(offset + limit)}
                                data-testid="users-next"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary px-4 py-1.5 disabled:opacity-40"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
